/* eslint-disable */

import axios from 'axios';
import axiosConfig from '../axiosConfig';
import {
	ChatRequest,
	ChatResponse,
	ChatSession,
	ChatSessionHistory,
	ChatSessionsResponse,
	GenericResponse,
	InovationChatRequest,
	InovationChatResponse,
	Reasoning,
} from '../types';

/**
 * ZAP-2318: citation target attached to a `tool_start`/`tool_result` frame (and
 * the `reasoning` echo) for `retrieve_data` / `get_dataframe` / `get_dashboard`
 * calls — absent for other tools. Drives the "Open in ZeroedIn" link on the
 * tool-call card.
 */
export interface ToolSource {
	type: 'dataframe' | 'dashboard' | 'chart' | 'table';
	id: number | string;
	title?: string;
}

/**
 * ZAP-2532: result summary attached to a `tool_result` SSE event. All keys are
 * optional — the renderer must tolerate any subset (or none) of them.
 */
export interface ToolResultSummary {
	rows?: number;
	columns?: string[];
	preview?: (string | number)[][];
	count?: number;
	titles?: string[];
	chart_type?: string;
	link?: string;
	bytes?: number;
	[key: string]: unknown;
}

/** ZAP-2532: payload of a `tool_result` SSE event. */
export interface InovationToolResultEvent {
	name: string;
	/** the model's toolUseId — same id across one call's tool_start/tool_result/reasoning frames */
	id?: string;
	step?: string;
	status?: 'success' | 'error';
	summary?: ToolResultSummary;
	details?: Record<string, unknown>;
	/** ZAP-2318: citation target for retrieve_data/get_dataframe/get_dashboard */
	source?: ToolSource;
}

/**
 * ZAP-2461: handlers for the SSE streaming endpoint (POST {cfBase}/stream).
 * Events are framed as `event: <type>\ndata: <json>\n\n`.
 */
export interface InovationStreamHandlers {
	onStart?: (data: { session_id: string }) => void;
	onToken?: (data: { text: string }) => void;
	onToolStart?: (data: {
		name: string;
		step: string;
		/** ZAP-2532: the model's toolUseId — shared across this call's frames */
		id?: string;
		details?: Record<string, unknown>;
		/** ZAP-2318: citation target for retrieve_data/get_dataframe/get_dashboard */
		source?: ToolSource;
	}) => void;
	/** ZAP-2532: a tool call finished — carries a compact result summary. */
	onToolResult?: (data: InovationToolResultEvent) => void;
	onReasoning?: (data: {
		step: string;
		/** ZAP-2532: toolUseId when this reasoning step belongs to a tool call */
		id?: string;
		details?: Record<string, unknown>;
	}) => void;
	onDone?: (data: {
		session_id: string;
		response: string;
		status: 'complete';
		reasoning?: Reasoning[];
		refreshed_token?: string;
		/** ZAP-2458: structured Highcharts config from the last successful format_* tool call */
		chart?: any;
	}) => void;
	onError?: (data: { message: string }) => void;
}

const API_KEY =
	process.env.REACT_APP_INNOVATION_AI_API_KEY ??
	'c2051712053c930e9345e66e24a58de140f33e8be8ba6c01b242358774ef4f81';

// Returns the first segment of the hostname (before the first '.')
const getZonePrefix = (): string => {
	try {
		const hostname =
			window.location.hostname ||
			(window.location.host && window.location.host.split(':')[0]) ||
			'';
		return hostname.split('.')[0] ?? '';
	} catch (e) {
		return '';
	}
};

const cfBase = (() => {
	const envUrl = process.env.REACT_APP_INNOVATION_AI_API_URL;
	if (window.location.host.includes('.zeroedin.com:7776')) {
		return 'https://ai-analyst.zeroedin-dev.com/chat';
	}
	return envUrl ?? 'https://ai-analyst.zeroedin-dev.com/chat';
})();

let cachedToken: { token: string; expires_at: number } | null = null;

/**
 * Fetches a Bearer token from the token-exchange endpoint.
 * Caches the token until it expires.
 */
async function getBearerToken(): Promise<string> {
	const now = Date.now();

	// Return cached token if still valid (with 1-minute buffer before expiry)
	if (cachedToken && cachedToken.expires_at > now + 60000) {
		return cachedToken.token;
	}

	try {
		const response = await axiosConfig.instance.post('app/mcp/token-exchange');
		const data = response.data;

		const token = data.token || data.data?.token;
		const expiresAt = new Date(data.expires_at || data.data?.expires_at).getTime();

		cachedToken = { token, expires_at: expiresAt };
		return token;
	} catch (err) {
		console.error('Failed to fetch Bearer token:', err);
		throw new Error('Unable to obtain Bearer token for Innovation AI');
	}
}

// Module-level throttle so repeated mounts (e.g. hero panel remounts) don't spam warmup
let lastWarmupAt = 0;
const WARMUP_THROTTLE_MS = 60000;

export class ChatActions {
	/**
	 * Warms up the AI Analyst request path so the first real message is faster:
	 *  - prefetches the Bearer token (moves the token exchange off the first-message critical path)
	 *  - fires an unauthenticated GET to the service /health endpoint to open the TLS connection
	 *
	 * Fire-and-forget: all failures are swallowed. Throttled module-wide to once per minute.
	 *
	 * NOTE: this warms TLS/token only; true AgentCore container warm-keeping is ZIS-161.
	 */
	public static warmup(): void {
		const now = Date.now();
		if (now - lastWarmupAt < WARMUP_THROTTLE_MS) return;
		lastWarmupAt = now;

		void getBearerToken().catch(() => {});

		try {
			// cfBase ends in '/chat' — health lives at the origin root
			const healthUrl = `${new URL(cfBase).origin}/health`;
			void axios.get(healthUrl, { withCredentials: false, timeout: 15000 }).catch(() => {});
		} catch (e) {
			// ignore malformed base URL
		}
	}

	public static SendMessage(request: ChatRequest): Promise<GenericResponse<ChatResponse>> {
		const url = 'app/ai/chat';

		return axiosConfig.instance.post(url, request).then((res) => {
			return res.data as GenericResponse<ChatResponse>;
		});
	}

	/**
	 * Sends a message to the Innovation AI endpoint (used by Analyst AI page).
	 * Base URL is constructed from env vars:
	 *  - REACT_APP_INNOVATION_AI_API_ID
	 *  - REACT_APP_INNOVATION_AI_ENVIRONMENT
	 * Falls back to the hardcoded URL if env vars are missing.
	 */
	public static async sendInovationAIMessage(
		request: InovationChatRequest
	): Promise<InovationChatResponse> {
		const cfUrl = cfBase;

		// Minimal axios to avoid XSRF header and cookies
		const directAxios = axios.create({
			withCredentials: false,
			timeout: 180000,
		});

		try {
			const bearerToken = await getBearerToken();

			return directAxios
				.post(cfUrl, request, {
					headers: {
						'Content-Type': 'application/json',
						'Authorization': `Bearer ${bearerToken}`,
						'x-actor-id': request.userId,
						'zonePrefix': getZonePrefix(),
					},
				})
				.then((res) => {
					const data = res.data;
					if (res.status === 202) {
						const jobId = data?.job_id ?? data?.jobId ?? data?.job ?? '';
						return {
							session_id: data?.session_id ?? '',
							job_id: jobId,
							response: 'the response is taking longer than expected to complete',
							status: 'pending',
							trace: data?.trace ?? [],
						} as InovationChatResponse;
					}

					if (typeof data === 'string') {
						return {
							session_id: '',
							response: data,
							status: 'complete',
							trace: [],
						} as InovationChatResponse;
					}

					return data as InovationChatResponse;
				})
				.catch((err) => {
					console.error('Direct CloudFront call failed:', err?.message ?? err);
					return {
						session_id: '',
						response: '',
						status: 'error',
						trace: [],
					} as InovationChatResponse;
				});
		} catch (err) {
			console.error('Failed to obtain Bearer token:', err);
			return {
				session_id: '',
				response: '',
				status: 'error',
				trace: [],
			} as InovationChatResponse;
		}
	}

	/**
	 * ZAP-2461: Sends a message to the Innovation AI SSE streaming endpoint and
	 * dispatches parsed events to the supplied handlers.
	 *
	 * Same JSON body and headers as sendInovationAIMessage; response is
	 * `text/event-stream`. Comment lines starting with ':' are heartbeats and
	 * are ignored.
	 *
	 * Rejects (so the caller can fall back to the non-streaming call) when:
	 *  - fetch throws before any event was received
	 *  - the response is not ok / not text/event-stream / has no body
	 *  - the stream closes before any event was dispatched
	 *
	 * Once at least one event has been dispatched, later failures (including the
	 * stream closing without a `done` event) are surfaced via onError and the
	 * promise resolves. An abort via the AbortSignal resolves quietly — the
	 * caller owns abort semantics.
	 */
	public static async sendInovationAIMessageStream(
		request: InovationChatRequest,
		handlers: InovationStreamHandlers,
		signal?: AbortSignal
	): Promise<void> {
		const streamUrl = cfBase.endsWith('/chat') ? `${cfBase}/stream` : `${cfBase}/chat/stream`;

		const bearerToken = await getBearerToken();

		const response = await fetch(streamUrl, {
			method: 'POST',
			body: JSON.stringify(request),
			signal,
			credentials: 'omit',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${bearerToken}`,
				'x-actor-id': request.userId ?? '',
				'zonePrefix': getZonePrefix(),
			},
		});

		const contentType = response.headers.get('content-type') ?? '';
		if (!response.ok || !contentType.includes('text/event-stream') || !response.body) {
			throw new Error(
				`AI stream unavailable (status ${response.status}, content-type "${contentType}")`
			);
		}

		const reader = response.body.getReader();
		const decoder = new TextDecoder();
		let buffer = '';
		let sawEvent = false;
		let finished = false; // saw `done` or `error`

		// Parses one SSE frame (the text between two blank lines) and dispatches it.
		const dispatchFrame = (frame: string) => {
			let eventType = 'message';
			const dataLines: string[] = [];

			for (const line of frame.split('\n')) {
				if (!line || line.startsWith(':')) continue; // heartbeat / comment
				if (line.startsWith('event:')) {
					eventType = line.slice(6).trim();
				} else if (line.startsWith('data:')) {
					// multi-line data: fields are joined with '\n' per the SSE spec
					dataLines.push(line.slice(5).replace(/^ /, ''));
				}
			}

			if (dataLines.length === 0) return;

			let payload: any;
			try {
				payload = JSON.parse(dataLines.join('\n'));
			} catch (e) {
				return; // ignore unparseable frames
			}

			sawEvent = true;

			switch (eventType) {
				case 'start':
					handlers.onStart?.(payload);
					break;
				case 'token':
					handlers.onToken?.(payload);
					break;
				case 'tool_start':
					handlers.onToolStart?.(payload);
					break;
				case 'tool_result':
					handlers.onToolResult?.(payload);
					break;
				case 'reasoning':
					handlers.onReasoning?.(payload);
					break;
				case 'done':
					finished = true;
					handlers.onDone?.(payload);
					break;
				case 'error':
					finished = true;
					handlers.onError?.(payload);
					break;
				default:
					break; // unknown event types are ignored
			}
		};

		try {
			for (;;) {
				const { done, value } = await reader.read();
				if (done) break;

				buffer += decoder.decode(value, { stream: true });
				// Normalize CRLF to LF. A lone trailing '\r' may be half of a CRLF
				// pair split across chunks — it stays in the buffer and is
				// normalized once the next chunk arrives.
				buffer = buffer.replace(/\r\n/g, '\n');

				let sepIdx: number;
				while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
					const frame = buffer.slice(0, sepIdx);
					buffer = buffer.slice(sepIdx + 2);
					dispatchFrame(frame);
					if (finished) {
						try {
							void reader.cancel();
						} catch (e) {
							// ignore
						}
						return;
					}
				}
			}
		} catch (err) {
			if (signal?.aborted) return; // caller-initiated abort — resolve quietly
			if (!sawEvent) throw err; // nothing shown yet — let the caller fall back
			handlers.onError?.({ message: (err as Error)?.message ?? 'AI stream failed' });
			return;
		}

		// Stream closed. Flush any final unterminated frame.
		if (buffer.trim().length > 0) {
			dispatchFrame(buffer);
		}
		if (finished || signal?.aborted) return;

		if (!sawEvent) {
			throw new Error('AI stream closed before any event');
		}

		// The stream closed without a `done` event — treat as an error.
		handlers.onError?.({ message: 'AI stream ended unexpectedly' });
	}

	/**
	 * Polls the Innovation AI status endpoint for a long-running job_id until completion.
	 */
	// NOT IN USE
	public static async getInovationAIStatus(jobId: string): Promise<InovationChatResponse> {
		const cfBaseUrl =
			process.env.REACT_APP_INNOVATION_AI_API_URL ??
			'https://ai-analyst.zeroedin-dev.com/chat';
		const statusUrl = cfBaseUrl.endsWith('/chat')
			? `${cfBaseUrl}/status`
			: `${cfBaseUrl}/chat/status`;

		const directAxios = axios.create({
			withCredentials: false,
			timeout: 100000,
			xsrfCookieName: '',
			xsrfHeaderName: '',
		});

		try {
			const bearerToken = await getBearerToken();

			return directAxios
				.get(statusUrl, {
					params: { job_id: jobId },
					headers: {
						Authorization: `Bearer ${bearerToken}`,
						zonePrefix: getZonePrefix(),
					},
				})
				.then((res) => {
					const data = res.data;
					if (res.status === 200) {
						if (typeof data === 'string') {
							return {
								session_id: '',
								response: data,
								status: 'complete',
								trace: [],
							} as InovationChatResponse;
						}

						return {
							session_id: data?.session_id ?? '',
							job_id: data?.job_id ?? jobId,
							response: data?.response ?? (typeof data === 'string' ? data : ''),
							status:
								data?.status === 'complete'
									? 'complete'
									: data?.status ?? 'pending',
							trace: data?.trace ?? [],
						} as InovationChatResponse;
					}

					return {
						session_id: '',
						response: '',
						status: 'error',
						trace: [],
					} as InovationChatResponse;
				})
				.catch((err) => {
					console.error('Direct status check failed:', err?.message ?? err);
					return {
						session_id: '',
						response: '',
						status: 'error',
						trace: [],
					} as InovationChatResponse;
				});
		} catch (err) {
			console.error('Failed to obtain Bearer token:', err);
			return {
				session_id: '',
				response: '',
				status: 'error',
				trace: [],
			} as InovationChatResponse;
		}
	}

	public static async getInovationAIChatHistory(
		jobId: string,
		sessionId: string
	): Promise<ChatSession> {
		// ZAP-2462: on a fresh Analyst page there's no session yet — calling with
		// an empty session_id 400s ("Direct status check failed") on the backend.
		// Skip the request and hand back the same "no history" shape the caller
		// already treats as empty, instead of round-tripping to a guaranteed 400.
		if (!sessionId) {
			return { session_id: '', messages: [] } as ChatSession;
		}

		let statusUrl = cfBase.endsWith('/chat') ? `${cfBase}/history` : `${cfBase}/chat/history`;

		const directAxios = axios.create({
			withCredentials: false,
			timeout: 180000,
			xsrfCookieName: '',
			xsrfHeaderName: '',
		});

		try {
			const bearerToken = await getBearerToken();

			return directAxios
				.get(statusUrl, {
					params: {
						job_id: jobId,
						session_id: sessionId,
					},
					headers: {
						Authorization: `Bearer ${bearerToken}`,
						zonePrefix: getZonePrefix(),
					},
				})
				.then((res) => {
					const data = res.data;

					if (res.status === 200) {
						if (typeof data === 'string') {
							return {
								session_id: '',
								messages: [
									{
										role: 'assistant',
										content: data,
										timestamp: Date.now(),
									},
								],
							} as ChatSession;
						}

						return {
							session_id: data?.session_id ?? '',
							messages: data?.messages ?? [],
						} as ChatSession;
					}

					return {
						session_id: '',
						messages: [],
					} as ChatSession;
				})
				.catch((err) => {
					console.error('Direct status check failed:', err?.message ?? err);

					return {
						session_id: '',
						messages: [],
					} as ChatSession;
				});
		} catch (err) {
			console.error('Failed to obtain Bearer token:', err);
			return {
				session_id: '',
				messages: [],
			} as ChatSession;
		}
	}

	/**
	 * Get all chat sessions for the current user
	 * Currently mocked - will switch to real endpoint when available
	 * Real endpoint: GET /chat/sessions
	 */
	public static async getInovationAIChatSessions(
		limit = 25,
		userId = ''
	): Promise<ChatSessionsResponse> {
		const sessionsUrl = cfBase.endsWith('/chat')
			? `${cfBase}/sessions`
			: `${cfBase}/chat/sessions`;

		const directAxios = axios.create({
			withCredentials: false,
			timeout: 180000,
			xsrfCookieName: '',
			xsrfHeaderName: '',
		});

		try {
			const bearerToken = await getBearerToken();

			return directAxios
				.get(sessionsUrl, {
					params: {
						limit,
					},
					headers: {
						'x-actor-id': userId,
						'Authorization': `Bearer ${bearerToken}`,
						'zonePrefix': getZonePrefix(),
					},
				})
				.then((res) => {
					const data = res.data;

					if (res.status === 200) {
						return {
							sessions: data?.sessions ?? [],
						} as ChatSessionsResponse;
					}

					return {
						sessions: [],
					} as ChatSessionsResponse;
				})
				.catch((err) => {
					console.error('Failed to fetch chat sessions:', err?.message ?? err);

					return {
						sessions: [],
					} as ChatSessionsResponse;
				});
		} catch (err) {
			console.error('Failed to obtain Bearer token:', err);
			return {
				sessions: [],
			} as ChatSessionsResponse;
		}
	}

	/**
	 * Delete a chat session by ID
	 * Real endpoint: DELETE /chat/sessions/:sessionId
	 */
	public static async deleteInovationAIChatSession(
		sessionId: string,
		userId = ''
	): Promise<boolean> {
		const deleteUrl = cfBase.endsWith('/chat')
			? `${cfBase}/sessions/${sessionId}`
			: `${cfBase}/chat/sessions/${sessionId}`;

		const directAxios = axios.create({
			withCredentials: false,
			timeout: 180000,
			xsrfCookieName: '',
			xsrfHeaderName: '',
		});

		try {
			const bearerToken = await getBearerToken();

			return directAxios
				.delete(deleteUrl, {
					headers: {
						'x-actor-id': userId,
						'Authorization': `Bearer ${bearerToken}`,
						'zonePrefix': getZonePrefix(),
					},
				})
				.then((res) => {
					return res.status === 200 || res.status === 204;
				})
				.catch((err) => {
					console.error('Failed to delete chat session:', err?.message ?? err);
					return false;
				});
		} catch (err) {
			console.error('Failed to obtain Bearer token:', err);
			return false;
		}
	}
}
